import { useState, useContext, useEffect, useCallback, useRef } from 'react' 
import { ReactSortable } from 'react-sortablejs'

import ActionsDropdown from '../../UI/ActionsDropdown'
import CustomTooltip from '../../UI/CustomTooltip'
import { ResizableContactColumn, ContactsRow, ContactsActions, ColumnsFilter, CreateEditContactModal, ContactsImportModal } from '../'
import { GlobalContext, ContactsContext, TeamContext, ColumnsContext } from '../../../context'
import { getTeamPaginationData, getTeamColumnsOrderData, getTeamColumnsData, isCookieAccepted, saveTeamPaginationDataToLS, saveTeamColumnsOrderDataToLS, removeTeamColumnsOrderData, saveTeamContactsColumnWidthsToLS } from '../../../utils'
import { perPageOptions } from '../../../constants'

const ContactsView = ({ filteredContacts, onSort, selectedContacts, setSelectedContacts, allChecked, onAllCheckedChange, paginatedData, setPaginatedData, refreshContacts, ready, setAllChecked }) => {
  const { t } = useContext(GlobalContext)
  const { setContactsSelectedFilters } = useContext(ContactsContext)
  const { selectedTeam } = useContext(TeamContext)
  const { contactsTableWidth, setContactsTableWidth, contactsTableColumnsWidths, setContactsTableColumnsWidths } = useContext(ColumnsContext)
  const [defaultFields, setDefaultFields] = useState([])
  const [columns, setColumns] = useState([
    { label: t('contacts.first_name'), value: 'first_name', icon: 'text_fields', checked: true },
    { label: t('contacts.last_name'), value: 'last_name', icon: 'text_fields', checked: true },
    { label: t('contacts.email'), value: 'email', icon: 'alternate_email', checked: true },
    { label: t('contacts.title'), value: 'title', icon: 'text_fields', checked: true },
    { label: t('contacts.address'), value: 'address', icon: 'home', checked: true },
    { label: t('contacts.street_number'), value: 'street_number', icon: 'looks_one', checked: true },
    { label: t('contacts.postcode'), value: 'zip_code', icon: 'looks_one', checked: true },
    { label: t('contacts.city'), value: 'city', icon: 'location_city', checked: true },
    { label: t('contacts.country'), value: 'country', icon: 'flag', checked: true },
    { label: t('contacts.phone_number'), value: 'phone_number', icon: 'smartphone', checked: true },
    { label: t('contacts.dob'), value: 'dob', icon: 'calendar_month', checked: true },
    { label: t('contacts.nationality'), value: 'nationality', icon: 'flag_circle', checked: true },
    { label: t('contacts.website'), value: 'website', icon: 'language', checked: true },
  ])
  const [search, setSearch] = useState('')
  const [perPage, setPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [allPages, setAllPages] = useState(1)
  const [paginationReady, setPaginationReady] = useState(false)
  const [columnsUpdated, setColumnsUpdated] = useState(false)
  const [activeResizableColumn, setActiveResizableColumn] = useState(null)
  const [tableWidthReady, setTableWidthReady] = useState(false)
  const [showImportCsvModal, setShowImportCsvModal] = useState(false)
  const [showImportXlsModal, setShowImportXlsModal] = useState(false)
  const [showCreateContactModal, setShowCreateContactModal] = useState(false)
  const colRefs = useRef([])

  // Check in LS to see if pagination data was saved and if it is saved update pagination state variables
  useEffect(() => {
    const paginationData = getTeamPaginationData()
    if(selectedTeam && paginationData && paginationData[selectedTeam.id] && paginationData[selectedTeam.id].contacts) {
      const data = paginationData[selectedTeam.id].contacts
      let all = Math.ceil(filteredContacts.length / data.items || 10)
      data.items && setPerPage(data.items)
      data.current && setCurrentPage(data.current <= all ? data.current : 1)
      data.all && setAllPages(data.all)
    }
  }, [filteredContacts, selectedTeam])

  // order columns...
  useEffect(() => {
    if(selectedTeam && !columnsUpdated) {
      let shouldUpdate = false
      let newColumns = [...columns]

      // Add custom fields to the table
      let teamContactsCustomColumns = selectedTeam.contacts_custom_columns
      if(teamContactsCustomColumns) {
        let arr = []
        for(let key in teamContactsCustomColumns) {
          arr.push({ 
            value: key, 
            label: teamContactsCustomColumns[key], 
            icon: 'temp_preferences_custom',
            checked: true 
          })
        }
        newColumns.push(...arr)
      }

      setDefaultFields([...newColumns, { label: '', value: 'helper', icon: '', checked: true }])

      // Show only checked columns
      const teamColData = getTeamColumnsData()
      if(teamColData && teamColData[selectedTeam.id]) {
        const colsValues = teamColData[selectedTeam.id].contacts
        let arr = []
        if(colsValues) {
          arr = newColumns.map(c => colsValues.includes(c.value) ? {...c, checked: true} : {...c, checked: c.value !== 'first_name' ? false : true})
          newColumns = arr
        }
      }

      // Change order
      const colOrderValues = getTeamColumnsOrderData()
      if(colOrderValues && colOrderValues[selectedTeam.id]) {
        const collectionColOrderValues = colOrderValues[selectedTeam.id].contacts 
        if(collectionColOrderValues) {
          let orderedCols = [] 
          collectionColOrderValues.forEach(val => {
            const col = newColumns.find(c => c.value === val)
            col && orderedCols.push(col)
          })
          // Check if there are some columns that were not saved to columns order array, if so add them 
          const filterHiddenValues = [] 
          newColumns.forEach(c => {
            if(!collectionColOrderValues.includes(c.value)) {
              filterHiddenValues.push(c)
            }
          })
          if(filterHiddenValues.length > 0) {
            orderedCols.push(...filterHiddenValues)
          }
          newColumns = orderedCols
          shouldUpdate = true
        }
      }

      // Add helper column
      newColumns.push({ label: '', value: 'helper', icon: '', checked: true })
      shouldUpdate = true

      // Update only if new columns are added or order changed
      if(shouldUpdate) {
        setColumns(newColumns)
      }
      setColumnsUpdated(true)
    }
  }, [selectedTeam, columnsUpdated, columns, t])

  // Calculate table width
  const calculateTableWidth = useCallback(() => {
    let width = 0
    let columnsSaved = false
    let savedColumns = []
    const carbonColumns = getTeamColumnsData()
    if(carbonColumns && carbonColumns[selectedTeam?.id]) {
      const cols = carbonColumns[selectedTeam?.id].contacts
      if(cols) {
        columnsSaved = true
        savedColumns = cols
      }
    }
    
    columns.forEach(col => {
      if(col.value !== 'helper') {
        if(columnsSaved) {
          if(savedColumns.includes(col.value)) {
            width += contactsTableColumnsWidths[col.value] || 200
          }
        }else {
          if(col.checked) {
            width += contactsTableColumnsWidths[col.value] || 200
          }
        }
      }
    })
    setContactsTableWidth(width)
  }, [columns, selectedTeam, setContactsTableWidth, contactsTableColumnsWidths])

  // Set width to each column and calculate table width
  useEffect(() => {
    if(!tableWidthReady) {
      calculateTableWidth()
      setTableWidthReady(true)
    }
  }, [calculateTableWidth, tableWidthReady])

  // Change paginated data arr when filtered arr changes
  useEffect(() => {
    let current = currentPage
    let sliceFrom = current === 1 ? 0 : (current - 1) * perPage
    let slicedData
    if(sliceFrom < filteredContacts.length) {
      slicedData = filteredContacts.slice(sliceFrom, sliceFrom + perPage)
    }else {
      slicedData = filteredContacts.slice(0, perPage)
    }
    setPaginatedData(slicedData)
    let all = Math.ceil(filteredContacts.length / perPage)
    setAllPages(all === 0 ? 1 : all)
    setTimeout(() => setPaginationReady(true), 200)
  }, [filteredContacts, perPage, currentPage, allPages, setPaginatedData])

  // On search change
  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setContactsSelectedFilters(prev => ({...prev, search: e.target.value}))
    setSelectedContacts([])
  }

  // On prev button click 
  const handlePrevButtonClick = (e) => {
    e.preventDefault()
    if(currentPage === 1) return 
    setCurrentPage(currentPage - 1)
    setSelectedContacts([])
    setAllChecked(false)
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, currentPage - 1, allPages, 'contacts')
    }
  }

  // On next button click 
  const handleNextButtonClick = (e) => {
    e.preventDefault()
    if(currentPage === allPages) return 
    setCurrentPage(currentPage + 1)
    setSelectedContacts([])
    setAllChecked(false)
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, currentPage + 1, allPages, 'contacts')
    }
  }

  // On go to specific page
  const handleGoToPage = (e, page) => {
    e.preventDefault()
    if(currentPage === page) return 
    setCurrentPage(page)
    setSelectedContacts([])
    setAllChecked(false)
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, page, allPages, 'contacts')
    }
  }

  // Per page change
  const handlePerPageChange = (e, num) => {
    e.preventDefault()
    setPerPage(num)
    setSelectedContacts([])
    setAllChecked(false)
    if(Math.ceil(filteredContacts.length / num) < currentPage) {
      setCurrentPage(1)
    }
    window.document.body.click()
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, num, currentPage, allPages, 'contacts')
    }
  }

  // Save columns order
  const handleSaveColumnsOrder = useCallback(() => {
    const colOrderValues = getTeamColumnsOrderData()
    if(isCookieAccepted()) {
      let arr = columns.map(f => f.value)
      saveTeamColumnsOrderDataToLS(selectedTeam?.id, arr, 'contacts')
    }else {
      if(colOrderValues) {
        removeTeamColumnsOrderData()
      } 
    }
  }, [columns, selectedTeam])

  // On mouse down - set active resizable column
  const handleMouseDown = useCallback((idx, val) => {
    setActiveResizableColumn({ index: idx, value: val })
  }, [])

  // On mouse move - only when there is active resizable column
  const handleMouseMove = useCallback((e) => {
    if(selectedTeam && activeResizableColumn !== null) {
      const el = colRefs.current[activeResizableColumn.index]
      if(!el) return
      const rect = el.getBoundingClientRect()
      const width = e.clientX - rect.x

      if(width > 200) {
        el.style.width = width + 'px'
        let newWidths
        newWidths = {...contactsTableColumnsWidths}
        newWidths[activeResizableColumn.value] = width
        setContactsTableColumnsWidths(newWidths)

        let totalWidth = 0
        columns.forEach(col => {
          if(col.value !== 'helper') {
            totalWidth += newWidths[col.value] || 200
          }
        })
        setContactsTableWidth(totalWidth)
        if(isCookieAccepted()) {
          saveTeamContactsColumnWidthsToLS(selectedTeam.id, newWidths)
        }
      }
    }
    // eslint-disable-next-line
  }, [activeResizableColumn, columns, selectedTeam])

  // Add mouse event listeners
  useEffect(() => {
    const handleMouseUp = () => {
      setActiveResizableColumn(null)
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }

    if (activeResizableColumn !== null) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
    }
  }, [activeResizableColumn, handleMouseMove])

  // On import csv modal open
  const handleOpenImportCsvModal = (e) => {
    e.preventDefault()
    setShowImportCsvModal(true)
  }

  // On import csv modal close
  const handleCloseImportCsvModal = () => {
    setShowImportCsvModal(false)
  }

  // On import xls modal open
  const handleOpenImportXlsModal = (e) => {
    e.preventDefault()
    setShowImportXlsModal(true)
  }

  // On import xls modal close
  const handleCloseImportXlsModal = () => {
    setShowImportXlsModal(false)
  }

  // On create contact modal open
  const handleOpenCreateContactModal = (e) => {
    e.preventDefault()
    setShowCreateContactModal(true)
  }

  // On create contact modal close
  const handleCloseCreateContactModal = () => {
    setShowCreateContactModal(false)
  }

  return (
    <div className="list-view-v2">
      <div className="list-view-v2__head">
        <div className="search">
          <div className="icon">
            <span className="material-symbols-outlined weight-300">search</span>
          </div>
          <input type="text" value={search} onChange={handleSearchChange} />
        </div>
        <div className="per-page">
          <ActionsDropdown
            trigger={<a href="#/" className="text-style-4">{perPage} {t('general.per_page')} <span className="material-symbols-outlined">expand_more</span></a>}
            hideHeader
            dropdownClass="pagination-dropdown"
            width={120}
          >
            <ul>
              {perPageOptions.map((num, i) => (
                <li key={i}>
                  <div className="link">
                    <a href="/#" onClick={(e) => handlePerPageChange(e, num)}>{num}</a>
                  </div>
                </li>
              ))}
            </ul>
          </ActionsDropdown>
        </div>
        <div className="actions">
          <ActionsDropdown
            trigger={
              <div>
                <CustomTooltip content={t('contacts.add')} spaceSm>
                  <button className="icon-btn icon-btn--small">
                    <span className="material-symbols-outlined">add</span>
                  </button>
                </CustomTooltip>
              </div>
            }
            hideHeader
            width={200}
            dropdownClass="actions-dropdown-v2"
          > 
            <ul className="default-list default-list--small">
              <li>
                <a href="/" onClick={handleOpenImportCsvModal}>
                  {/* <span className="icon"><span className="material-symbols-outlined">edit</span></span> */}
                  {t('contacts.import_csv')}
                </a>
              </li>
              <li>
                <a href="/" onClick={handleOpenImportXlsModal}>
                  {/* <span className="icon"><span className="material-symbols-outlined">delete</span></span> */}
                  {t('contacts.import_xls')}
                </a>
              </li>
              <li>
                <a href="/" onClick={handleOpenCreateContactModal}>
                  {/* <span className="icon"><span className="material-symbols-outlined">delete</span></span> */}
                  {t('contacts.add_manually')}
                </a>
              </li>
            </ul>
          </ActionsDropdown>
        </div>
        <div className="filter">
          <ColumnsFilter 
            columns={columns} 
            setColumns={setColumns} 
            onOrderSave={handleSaveColumnsOrder} 
            view="contacts" 
            defaultFields={defaultFields}
            calculateTableWidth={calculateTableWidth}
          />
        </div>
      </div>

      <div className="list-view-v2__body">
        <div className="list-view-table-wrapper u-custom-scrollbar u-custom-scrollbar--3">
          {selectedContacts.length > 0 && (
            <ContactsActions allChecked={allChecked} onAllCheckedChange={onAllCheckedChange} setSelectedItems={setSelectedContacts} selectedItems={selectedContacts} refreshContacts={refreshContacts} />
          )}
          <div className="list-view-table" style={{minWidth: contactsTableWidth}}>
            <div className="list-view-table__head">
              <ReactSortable 
                list={columns} 
                setList={setColumns} 
                animation={200} 
                delayOnTouchStart={true} 
                delay={2} 
                dragClass="dragged-field--2"
                ghostClass="selected-field"
                filter=".filtered-out"
                draggable=".draggable-item"
                onEnd={handleSaveColumnsOrder}
                handle=".sort"      
                className="sortable-row-items"  
              >
                {columns.filter(c => c.checked).map((column, idx) => {
                  return (
                    <ResizableContactColumn column={column} idx={idx} onMouseDown={handleMouseDown} onSort={onSort} ref={(item) => colRefs.current[idx] = item} key={idx} />
                  )
                })}
              </ReactSortable>
              <div className="list-view-table__cell checkbox">
                {!allChecked 
                  ? <span className="material-symbols-outlined" onClick={onAllCheckedChange}>check_box_outline_blank</span>
                  : <span className="material-symbols-outlined filled" onClick={onAllCheckedChange}>check_box</span>
                }
              </div>
            </div>
            <div className="list-view-table__body">
              {(!ready || !paginationReady) && (
                <div className="loader-wrapper"><div className="loader-v2"></div></div>
              )}
              {ready && paginationReady && paginatedData.map((doc, idx) => {

                return (
                  <ContactsRow 
                    key={idx} 
                    doc={doc}
                    id={doc.id}
                    selectedContacts={selectedContacts}
                    setSelectedContacts={setSelectedContacts}
                    allChecked={allChecked}
                    columns={columns}
                    refreshContacts={refreshContacts}
                  />
                )
              })}
              {ready && paginationReady && paginatedData.length === 0 && (
                <div className="no-data">
                  <p className="text-style-3">{t('folder.empty')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="list-view-v2__foot">
        <div className="pagination-v2">
          <a href="/#" className="prev" onClick={handlePrevButtonClick}><span className="material-symbols-outlined weight-300">arrow_back</span></a>
          {Array(allPages).fill().map((_, idx) => {
            return (
              <a href="/#" key={idx} className={`${idx+1 === currentPage ? 'active' : ''}`} onClick={(e) => handleGoToPage(e, idx+1)}>
                {idx+1}
              </a>
            )
          })}
          <a href="/#" className="next" onClick={handleNextButtonClick}><span className="material-symbols-outlined weight-300">arrow_forward</span></a>
        </div>
      </div>

      {showCreateContactModal && (
        <CreateEditContactModal 
          onClose={handleCloseCreateContactModal}
          refreshContacts={refreshContacts}
        />
      )}

      {showImportCsvModal && (
        <ContactsImportModal 
          onClose={handleCloseImportCsvModal}
          refreshContacts={refreshContacts}
        />
      )}

      {showImportXlsModal && (
        <ContactsImportModal 
          onClose={handleCloseImportXlsModal}
          refreshContacts={refreshContacts}
          isXls={true}
        />
      )}
    </div>
  )
}

export default ContactsView